package com.karawek.core.auth.stytch

import com.karawek.core.auth.api.Authenticator
import com.karawek.core.auth.model.AuthMethod
import com.karawek.core.auth.model.AuthSession
import com.karawek.core.auth.model.AuthenticatedUser
import com.karawek.core.auth.model.AuthenticationInfo
import com.karawek.core.auth.model.OAuthProvider
import com.karawek.core.auth.model.OtpChannel
import com.karawek.core.auth.model.Passkey
import com.karawek.core.auth.model.OtpHandle
import com.karawek.core.auth.stytch.client.AuthenticationResponse
import com.karawek.core.auth.stytch.client.StytchClient

class StytchAuthenticator(
    private val stytchClient: StytchClient,
) : Authenticator {

    override suspend fun getAvailableMethods(): Set<AuthMethod> {
        val methods = mutableSetOf<AuthMethod>(
            OtpChannel.Email,
            OtpChannel.Phone,
            OAuthProvider.Google,
            OAuthProvider.Apple,
            OAuthProvider.Facebook,
        )
        stytchClient.getNativeProviderSupport()?.let { methods.add(it) }
        if (stytchClient.supportsPasskeys()) methods.add(Passkey)
        return methods
    }

    override suspend fun initiateOtp(
        channel: OtpChannel,
        value: String,
        loginOrCreate: Boolean,
    ): Result<OtpHandle> {
        return runCatching {
            val initiateOtpResponse = when (channel) {
                OtpChannel.Email ->
                    stytchClient.initiateEmailOtp(
                        email = value,
                    )
                OtpChannel.Phone ->
                    stytchClient.initiateSmsOtp(
                        phoneE164 = value,
                    )
                else -> throw UnsupportedOperationException("Unsupported OTP channel: $channel")
            }

            StytchOtpHandle(
                methodId = initiateOtpResponse.methodId,
            )
        }
    }

    override suspend fun verifyOtp(
        handle: OtpHandle,
        otpCode: String,
    ): Result<AuthenticationInfo> = runCatching {
        val stytchHandle = handle as? StytchOtpHandle
            ?: error("Invalid OTP handle for Stytch")
        stytchClient.verifyOtp(
            methodId = stytchHandle.methodId,
            otpCode = otpCode,
        ).toAuthenticationInfo()
    }

    override suspend fun authenticateWithOAuth(
        provider: OAuthProvider,
    ): Result<AuthenticationInfo> = runCatching {
        stytchClient.authenticateWithOAuth(
            provider = provider,
        ).toAuthenticationInfo()
    }

    override suspend fun authenticateWithNative(): Result<AuthenticationInfo> = runCatching {
        stytchClient.authenticateWithNative().toAuthenticationInfo()
    }

    override suspend fun signOut(): Result<Unit> = runCatching {
        stytchClient.signOut()
    }

    override suspend fun registerPasskey(): Result<Unit> = runCatching {
        stytchClient.registerPasskey()
    }

    override suspend fun authenticateWithPasskey(): Result<AuthenticationInfo> = runCatching {
        stytchClient.authenticateWithPasskey().toAuthenticationInfo()
    }

    override suspend fun renewSession(durationMinutes: Int): Result<Unit> = runCatching {
        stytchClient.renewSession(durationMinutes)
    }

    fun AuthenticationResponse.toAuthenticationInfo(): AuthenticationInfo = AuthenticationInfo(
        session = AuthSession(
            userId = userId,
            sessionToken = sessionToken,
            sessionJwt = sessionJwt,
            sessionId = sessionId,
            sessionExpiration = expiresAt,
        ),
        user = AuthenticatedUser(
            firstName = firstName,
            middleName = middleName,
            lastName = lastName,
            emailAddresses = emailAddresses,
            phoneNumbers = phoneNumbers,
            profilePictureUrl = profilePictureUrl,
        )
    )
}
